import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { trigger, transition, style, animate, stagger, query, animateChild } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import { IncomeStreamType, IncomeSourceSubType, ArgyleViewBagResponse } from 'src/app/models/Argyle';
import { DraftIncomeSource, WizardWorkflowState } from 'src/app/models/income-wizard-workflow';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';
import { IncomeAuditService } from 'src/app/audit-module/services/income-audit.service';
import { normalizeArgyleIncomeStream, sortStreamsForWizard, draftKeyFromStream } from './income-wizard-stream-utils';
import { RiqAlertComponent } from 'src/app/shared-components-module/riq-alert/riq-alert.component';

export interface FindingFormState {
  draftId: number;
  defectId: number | null;
  severity: number;
  description: string;
  saving: boolean;
  error: string | null;
}

@Component({
  selector: 'app-wizard-overview-phase',
  templateUrl: './wizard-overview-phase.component.html',
  styleUrls: ['./wizard-overview-phase.component.less'],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(16px)' }),
        animate('280ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('listStagger', [
      transition(':enter', [
        query('@cardEnter', stagger(60, animateChild()), { optional: true }),
      ]),
    ]),
    trigger('fadeSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px)' }),
        animate('320ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-8px)' })),
      ]),
    ]),
    trigger('celebrateIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.6)' }),
        animate('500ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class WizardOverviewPhaseComponent implements OnInit {
  @Input() screeningId!: number;
  @Input() argyleBag: ArgyleViewBagResponse | null = null;
  @Input() drafts: DraftIncomeSource[] = [];
  @Input() auditMode = false;
  @Input() disabled = false;
  @Output() phaseComplete = new EventEmitter<void>();
  @Output() workflowStateChange = new EventEmitter<WizardWorkflowState>();
  @Output() editStream = new EventEmitter<{ streamIndex: number; draftId: number | null }>();
  @Output() draftRemoved = new EventEmitter<void>();

  loading = false;
  loadError: string | null = null;
  actionError: string | null = null;
  actionBusy = false;
  finalizing = false;
  finalized = false;

  // Finding form state (audit mode only)
  findingForm: FindingFormState | null = null;
  defectOptions: { id: number; defectName: string }[] = [];

  // Stream keys for mapping draft → stream index (needed for edit navigation).
  private _streamKeys: string[] = [];

  constructor(private appData: ApplicantDataService, private modal: NgbModal, private auditService: IncomeAuditService) {}

  ngOnInit(): void {
    // Build stream keys from the parent-provided bag (for edit navigation).
    const orderedStreams = sortStreamsForWizard(
      (this.argyleBag?.streams ?? []).map((s) => normalizeArgyleIncomeStream(s))
    );
    this._streamKeys = orderedStreams.map((s) => draftKeyFromStream(s));

    // In audit mode, load defect types for the finding form.
    if (this.auditMode) {
      this.auditService.getDefectsForReviewIncome(0).subscribe({
        next: (defects) => {
          this.defectOptions = defects.filter((def)=>{ return def.supportIssueTypeId == 7;}) ?? [];
        },
      });
    }
  }

  get includedDrafts(): DraftIncomeSource[] {
    return this.drafts.filter((d) => !d.isExcluded);
  }

  get findingDraftName(): string {
    if (!this.findingForm) return '';
    const draft = this.drafts.find((d) => d.id === this.findingForm!.draftId);
    return draft?.employerName || 'Income source';
  }

  get excludedDrafts(): DraftIncomeSource[] {
    return this.drafts.filter((d) => d.isExcluded);
  }

  get totalMonthly(): number {
    return this.includedDrafts.reduce((sum, d) => sum + (Number(d.incomeAmount) || 0), 0);
  }

  streamTypeLabel(d: DraftIncomeSource): string {
    switch (d.streamType) {
      case IncomeStreamType.PayrollSync: return 'Payroll Sync';
      case IncomeStreamType.BankSync: return 'Bank Account Sync';
      case IncomeStreamType.DocUpload: return 'DocUpload';
      case IncomeStreamType.WorkNumber: return 'EFX Work Number';
      default: return 'Unknown';
    }
  }

  streamSubTypeLabel(d: DraftIncomeSource): string {
    switch (d.subType) {
      case IncomeSourceSubType.Employment: return 'Employment Sync';
      case IncomeSourceSubType.Checkings: return 'Checking Sync';
      case IncomeSourceSubType.Savings: return 'Savings Sync';
      case IncomeSourceSubType.Paystubs: return 'Paystub Upload';
      case IncomeSourceSubType.W2: return 'W2 Upload';
      case IncomeSourceSubType.BankStatements: return 'Bank Statement Upload';
      case IncomeSourceSubType.Form1099: return '1099 Upload';
      default: return '';
    }
  }

  // onRemoveDraft(d: DraftIncomeSource): void {
  //   if (this.disabled || this.actionBusy) {
  //     return;
  //   }
  //   this.actionBusy = true;
  //   this.actionError = null;
  //   this.appData.deleteWizardDraftSource(d.id).subscribe({
  //     next: () => {
  //       this.actionBusy = false;
  //       this.draftRemoved.emit();
  //     },
  //     error: (err: unknown) => {
  //       this.actionBusy = false;
  //       this.actionError = this.httpErrorMessage(err, 'Could not remove draft.');
  //     },
  //   });
  // }

  onEditDraft(d: DraftIncomeSource): void {
    if (this.disabled || this.actionBusy) {
      return;
    }
    const idx = this.streamIndexForDraft(d);
    if (idx < 0) {
      this.actionError = 'Could not locate this stream.';
      return;
    }
    this.editStream.emit({ streamIndex: idx, draftId: d.id });
  }

  onUndoExclusion(d: DraftIncomeSource): void {
    if (this.disabled || this.actionBusy) {
      return;
    }
    const idx = this.streamIndexForDraft(d);
    if (idx < 0) {
      this.actionError = 'Could not locate this stream.';
      return;
    }
    this.editStream.emit({ streamIndex: idx, draftId: d.id });
  }

  openFinalizeConfirm(): void {
    if (this.disabled || this.actionBusy || this.finalizing) {
      return;
    }
    this.finalizing = true;
    setTimeout(() => {
      this.finalized = true;
      setTimeout(() => {
        this.phaseComplete.emit();
      }, 1200);
    }, 1500);
  }

  private streamIndexForDraft(d: DraftIncomeSource): number {
    if (d.streamType === IncomeStreamType.BankSync) {
      return this._streamKeys.findIndex((key) => d.streamIdentifier.startsWith(key + ':'));
    }
    return this._streamKeys.indexOf(d.streamIdentifier);
  }

  // ── Finding form (audit mode) ──

  openFindingForm(draft: DraftIncomeSource): void {
    this.findingForm = {
      draftId: draft.id,
      defectId: null,
      severity: 20, // default Medium
      description: '',
      saving: false,
      error: null,
    };
  }

  cancelFinding(): void {
    this.findingForm = null;
  }

  submitFinding(): void {
    if (!this.findingForm || !this.findingForm.defectId || !this.findingForm.description.trim()) return;
    this.findingForm.saving = true;
    this.findingForm.error = null;

    // Assign to the processor who reviewed this draft.
    const draft = this.drafts.find((d) => d.id === this.findingForm!.draftId);
    const assignedTo = draft?.reviewedByUserId ?? 0;

    this.auditService.createFinding(
      this.screeningId,
      this.findingForm.draftId,
      this.findingForm.defectId,
      this.findingForm.severity,
      this.findingForm.description.trim(),
      assignedTo
    ).subscribe({
      next: () => {
        this.findingForm = null;
      },
      error: () => {
        if (this.findingForm) {
          this.findingForm.saving = false;
          this.findingForm.error = 'Failed to create finding.';
        }
      },
    });
  }

  private httpErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (
        body &&
        typeof body === 'object' &&
        'response' in body &&
        typeof (body as { response?: unknown }).response === 'string'
      ) {
        return (body as { response: string }).response;
      }
      return err.message || fallback;
    }
    return fallback;
  }
}
